import { useId, useState } from 'react';
import { EDAD_MAXIMA, EDAD_MINIMA, edadEn } from '@medchoice/shared';
import { ErrorDeCampo } from './Campos';
import { Icono } from './Icono';
import { hoyIso } from './format';

const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

interface PropsFechaDeNacimiento {
  etiqueta?: string;
  /** 'AAAA-MM-DD', o vacío mientras falte elegir alguna parte. */
  value: string;
  alCambiar: (fecha: string) => void;
  onBlur?: () => void;
  error?: string | null;
  required?: boolean;
}

const diasDelMes = (anio: number, mes: number) => new Date(anio, mes, 0).getDate();

/**
 * Día, mes y año por separado. Con un calendario, llegar a 1980 son decenas de clics;
 * acá son tres elecciones, y el año solo ofrece los que dejan a la persona dentro de
 * la edad permitida.
 */
export function FechaDeNacimiento({ etiqueta = 'Fecha de nacimiento', value, alCambiar, onBlur, error, required }: PropsFechaDeNacimiento) {
  const id = useId();
  const [partes, setPartes] = useState(() => {
    const [a = '', m = '', d = ''] = value ? value.split('-') : [];
    return { dia: d, mes: m, anio: a };
  });
  const hoy = hoyIso();
  const anioActual = Number(hoy.slice(0, 4));
  const anios = Array.from({ length: EDAD_MAXIMA - EDAD_MINIMA + 1 }, (_, i) => String(anioActual - EDAD_MINIMA - i));
  const tope = partes.mes ? diasDelMes(Number(partes.anio || 2000), Number(partes.mes)) : 31;
  const dias = Array.from({ length: tope }, (_, i) => String(i + 1).padStart(2, '0'));

  const cambiar = (parte: keyof typeof partes, valor: string) => {
    const nuevas = { ...partes, [parte]: valor };
    // Si el día elegido no existe en el mes nuevo (31 de abril), se lo lleva al último.
    if (nuevas.dia && nuevas.mes) {
      const maximo = diasDelMes(Number(nuevas.anio || 2000), Number(nuevas.mes));
      if (Number(nuevas.dia) > maximo) nuevas.dia = String(maximo);
    }
    setPartes(nuevas);
    alCambiar(nuevas.dia && nuevas.mes && nuevas.anio ? `${nuevas.anio}-${nuevas.mes}-${nuevas.dia}` : '');
  };

  const edad = value ? edadEn(value, hoy) : null;
  // Con la fecha completa, la edad mínima se avisa enseguida, sin esperar a que se deje el campo.
  const mensaje = error ?? (edad !== null && edad < EDAD_MINIMA ? `Para usar MedChoice tenés que tener al menos ${EDAD_MINIMA} años.` : null);
  const selector = (parte: keyof typeof partes, marcador: string, opciones: { valor: string; texto: string }[], nombre: string) => (
    <div className={`campo__caja campo__caja--selector fecha-nacimiento__${parte}${mensaje ? ' campo__caja--error' : ''}`}>
      <select aria-label={nombre} className={partes[parte] ? 'campo__control' : 'campo__control campo__control--vacio'}
        value={partes[parte]} onChange={(e) => cambiar(parte, e.target.value)} onBlur={onBlur}
        aria-invalid={mensaje ? true : undefined} aria-describedby={`${id}-nota`}>
        <option value="">{marcador}</option>
        {opciones.map((o) => <option key={o.valor} value={o.valor}>{o.texto}</option>)}
      </select>
      <Icono nombre="chevron-down" tamano={16} className="campo__flecha" />
    </div>
  );

  return (
    <fieldset className="campo fecha-nacimiento" aria-describedby={`${id}-nota`}>
      <legend className="campo__etiqueta">
        {etiqueta}
        {required && <span className="campo__requerido" aria-hidden="true">*</span>}
      </legend>
      <div className="fecha-nacimiento__partes">
        {selector('dia', 'Día', dias.map((d) => ({ valor: d, texto: String(Number(d)) })), 'Día')}
        {selector('mes', 'Mes', MESES.map((m, i) => ({ valor: String(i + 1).padStart(2, '0'), texto: m })), 'Mes')}
        {selector('anio', 'Año', anios.map((a) => ({ valor: a, texto: a })), 'Año')}
      </div>
      {mensaje
        ? <ErrorDeCampo id={`${id}-nota`}>{mensaje}</ErrorDeCampo>
        : (
          <div id={`${id}-nota`} className="campo__nota">
            {edad !== null ? `Tenés ${edad} años.` : `MedChoice es para mayores de ${EDAD_MINIMA} años.`}
          </div>
        )}
    </fieldset>
  );
}
